import { useEffect, useState } from 'react'

export interface Toast {
  id: string
  title?: string
  description?: string
  action?: React.ReactNode
  variant?: 'default' | 'destructive'
}

// Previously this held its toast list in per-call `useState([])` — every
// component that called `useToast()` got its own independent array, so a
// `toast()` call from Playground/AdminPanel/Organizations never reached the
// single <Toaster/> mounted once in App.tsx (that instance's own useToast()
// call had a completely separate, always-empty list). Fixed the standard
// shadcn way: one module-level store, `dispatch()` updates it and notifies
// every subscribed component, so any `toast()` call — from any file — reaches
// every mounted <Toaster/>.
type ToastInput = Omit<Toast, 'id'>

let memoryState: Toast[] = []
const listeners: Array<(state: Toast[]) => void> = []
const timeouts = new Map<string, ReturnType<typeof setTimeout>>()

function notify() {
  listeners.forEach((listener) => listener(memoryState))
}

function dismiss(id: string) {
  const t = timeouts.get(id)
  if (t) { clearTimeout(t); timeouts.delete(id) }
  memoryState = memoryState.filter((t) => t.id !== id)
  notify()
}

export function toast(input: ToastInput): string {
  const id = Math.random().toString(36).substr(2, 9)
  memoryState = [...memoryState, { id, ...input }]
  notify()

  const timeout = setTimeout(() => dismiss(id), 5000)
  timeouts.set(id, timeout)

  return id
}

export function useToast() {
  const [toasts, setToasts] = useState<Toast[]>(memoryState)

  useEffect(() => {
    listeners.push(setToasts)
    return () => {
      const i = listeners.indexOf(setToasts)
      if (i > -1) listeners.splice(i, 1)
    }
  }, [])

  return { toasts, toast, dismiss }
}
